import m from "mithril";
import { getAuthToken, getUserData, getSyncUserData } from "../utils.js";
import { getTerm } from "../js/nomenclature.js";
import toast from "../js/toaster.js";

const Receipts = {
    paidInstallments: [],
    loading: true,

    oninit() {
        console.log('DEBUG: Receipts component oninit called');
        this.loadReceipts();
    },

    async loadReceipts() {
        console.log('DEBUG: loadReceipts function called');
        this.loading = true;
        try {
            const token = getAuthToken();
            const response = await fetch('/api/loan-installments-receipts', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch receipts: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                console.log('DEBUG: Full API response:', result);
                console.log('DEBUG: API response data array:', result.data);
                console.log('DEBUG: API response debug info:', result.debug);
                console.log('DEBUG: Receipts data received from API:', result.data);
                console.log('DEBUG: Number of installments received:', result.data.length);
                this.paidInstallments = result.data;

                // Log details of each payment including status
                result.data.forEach((payment, index) => {
                    console.log(`DEBUG: Payment ${index + 1}:`, {
                        payment_id: payment.payment_id,
                        payment_no: payment.payment_no,
                        installment_number: payment.installment_number,
                        paid_amount: payment.paid_amount,
                        principal_amount: payment.principal_amount,
                        interest_amount: payment.interest_amount,
                        status: payment.status, // Add status to debug log
                        customer_name: payment.customer_name,
                        payment_date: payment.payment_date
                    });
                });
            } else {
                throw new Error(result.error || 'Failed to load receipts');
            }
        } catch (error) {
            console.error('Error fetching receipts:', error);
            toast('Gagal memuat data tanda terima: ' + error.message, 'error');
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    generatePDF(installment) {
        try {
            const userData = getSyncUserData();
            const now = new Date();

            // Use the status from API response (already calculated with proper cumulative logic)
            const statusText = installment.status || 'SEBAGIAN';

            const receiptContent = `
                <html>
                <head>
                    <title>Tanda Terima - ${installment.customer_name}</title>
                    <style>
                        body {
                            font-family: 'Courier New', monospace;
                            margin: 0;
                            padding: 20px;
                            font-size: 12px;
                            width: 320px;
                            margin: 0 auto;
                        }
                        .receipt-header {
                            text-align: center;
                            margin-bottom: 20px;
                            border-bottom: 1px dashed #000;
                            padding-bottom: 10px;
                        }
                        .receipt-header h2 {
                            margin: 5px 0;
                            font-size: 16px;
                            font-weight: bold;
                        }
                        .receipt-header p {
                            margin: 2px 0;
                            font-size: 10px;
                        }
                        .section {
                            margin-bottom: 15px;
                        }
                        .section h3 {
                            margin: 5px 0;
                            font-size: 12px;
                            font-weight: bold;
                            text-decoration: underline;
                        }
                        .detail-row {
                            display: flex;
                            justify-content: space-between;
                            margin: 3px 0;
                            font-size: 11px;
                        }
                        .status {
                            text-align: center;
                            font-weight: bold;
                            font-size: 13px;
                            margin: 15px 0;
                            padding: 5px;
                            border: 1px solid #000;
                        }
                        .footer {
                            text-align: center;
                            margin-top: 20px;
                            font-size: 10px;
                            border-top: 1px dashed #000;
                            padding-top: 10px;
                        }
                        @media print {
                            body { margin: 0; }
                        }
                    </style>
                </head>
                <body>
                    <div class="receipt-header">
                        <h2>BUKTI PEMBAYARAN ANGSURAN</h2>
                        <p>VITTA - Simpan Pinjam</p>
                        <p>Tanggal: ${this.formatDate(now)}</p>
                        <p>Waktu: ${now.toLocaleTimeString('id-ID')}</p>
                    </div>

                    <div class="section">
                        <h3>NASABAH</h3>
                        <div class="detail-row">
                            <span>Nama:</span>
                            <span>${installment.customer_name}</span>
                        </div>
                        <div class="detail-row">
                            <span>No. Reg:</span>
                            <span>${installment.customer_no_reg}</span>
                        </div>
                    </div>

                    <div class="section">
                        <h3>DETAIL PEMBAYARAN</h3>
                        <div class="detail-row">
                            <span>Angsuran Ke:</span>
                            <span>${installment.installment_number}</span>
                        </div>
                        <div class="detail-row">
                            <span>Jumlah Dibayar:</span>
                            <span>${this.formatCurrency(installment.paid_amount || 0)}</span>
                        </div>
                        <div class="detail-row">
                            <span>Pokok:</span>
                            <span>${this.formatCurrency(installment.principal_amount || 0)}</span>
                        </div>
                        <div class="detail-row">
                            <span>${getTerm('fields.interest', 'Bunga')}:</span>
                            <span>${this.formatCurrency(installment.interest_amount || 0)}</span>
                        </div>
                    </div>

                    <div class="status">
                        STATUS: ${statusText}
                    </div>

                    <div class="footer">
                        <p>Terima kasih atas pembayaran Anda.</p>
                        <p>Koordinator: ${getSyncUserData()?.fullName || 'Unknown'}</p>
                    </div>
                </body>
                </html>
            `;

            // Open in new window for printing
            const printWindow = window.open('', '_blank', 'width=400,height=600');
            if (printWindow) {
                printWindow.document.write(receiptContent);
                printWindow.document.close();
                printWindow.focus();

                // Auto print (optional)
                setTimeout(() => {
                    printWindow.print();
                }, 500);
            } else {
                alert('Popup blocked! Please allow popups to print receipts.');
            }

            toast('Tanda terima berhasil dibuat', 'success');
        } catch (error) {
            console.error('Error generating receipt:', error);
            toast('Gagal membuat tanda terima: ' + error.message, 'error');
        }
    },

    generateAllReceiptsPDF() {
        // Generate combined HTML receipt
        this.generateCombinedReceipt();
    },

    generateCombinedReceipt() {
        try {
            const now = new Date();
            const userData = getSyncUserData();

            const tableRows = this.paidInstallments.map((installment, index) => `
                <tr>
                    <td>${index + 1}</td>
                    <td>${installment.customer_name}</td>
                    <td>Ke-${installment.installment_number}</td>
                    <td>${this.formatCurrency(installment.paid_amount || 0)}</td>
                    <td>${this.formatDate(installment.payment_date)}</td>
                    <td>${(() => {
                    return installment.status || 'SEBAGIAN';
                })()}</td>
                </tr>
            `).join('');

            const receiptContent = `
                <html>
                <head>
                    <title>Daftar Tanda Terima - ${userData?.fullName || 'Unknown'}</title>
                    <style>
                        body {
                            font-family: 'Courier New', monospace;
                            margin: 0;
                            padding: 20px;
                            font-size: 11px;
                            max-width: 800px;
                            margin: 0 auto;
                        }
                        .receipt-header {
                            text-align: center;
                            margin-bottom: 20px;
                            border-bottom: 2px solid #000;
                            padding-bottom: 15px;
                        }
                        .receipt-header h2 {
                            margin: 5px 0;
                            font-size: 18px;
                            font-weight: bold;
                        }
                        .receipt-header p {
                            margin: 2px 0;
                            font-size: 10px;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 20px;
                            font-size: 10px;
                        }
                        th, td {
                            border: 1px solid #000;
                            padding: 8px 5px;
                            text-align: left;
                        }
                        th {
                            background-color: #f0f0f0;
                            font-weight: bold;
                            text-align: center;
                        }
                        .footer {
                            text-align: center;
                            margin-top: 30px;
                            font-size: 9px;
                            border-top: 1px solid #000;
                            padding-top: 15px;
                        }
                        @media print {
                            body { margin: 0; }
                            table { page-break-inside: avoid; }
                        }
                    </style>
                </head>
                <body>
                    <div class="receipt-header">
                        <h2>DAFTAR TANDA TERIMA PEMBAYARAN</h2>
                        <p>VITTA - Simpan Pinjam</p>
                        <p>Tanggal: ${this.formatDate(now)}</p>
                        <p>Koordinator: ${userData?.fullName || 'Unknown'}</p>
                    </div>

                    <table>
                        <thead>
                            <tr>
                                <th style="width: 5%;">No</th>
                                <th style="width: 30%;">Nasabah</th>
                                <th style="width: 15%;">Angsuran</th>
                                <th style="width: 20%;">Jumlah Dibayar</th>
                                <th style="width: 15%;">Tanggal Bayar</th>
                                <th style="width: 15%;">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${tableRows}
                        </tbody>
                    </table>

                    <div class="footer">
                        <p>Total Pembayaran: ${this.paidInstallments.length} transaksi</p>
                        <p>Dicetak pada: ${now.toLocaleString('id-ID')}</p>
                    </div>
                </body>
                </html>
            `;

            // Open in new window for printing
            const printWindow = window.open('', '_blank', 'width=1000,height=700');
            if (printWindow) {
                printWindow.document.write(receiptContent);
                printWindow.document.close();
                printWindow.focus();

                // Auto print (optional)
                setTimeout(() => {
                    printWindow.print();
                }, 500);
            } else {
                alert('Popup blocked! Please allow popups to print receipts.');
            }

            toast('Daftar tanda terima berhasil dibuat', 'success');
        } catch (error) {
            console.error('Error generating combined receipt:', error);
            toast('Gagal membuat daftar tanda terima: ' + error.message, 'error');
        }
    },

    formatDate(dateString) {
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    },

    formatCurrency(amount) {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR'
        }).format(amount || 0);
    },


    view() {
        return m("div", { class: "min-h-screen bg-gray-50 dark:bg-gray-900" }, [
            // Header
            m("div", { class: "bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700" }, [
                m("div", { class: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" }, [
                    m("div", { class: "flex justify-between items-center py-4" }, [
                        m("div", { class: "flex items-center" }, [
                            m("button", {
                                class: "mr-4 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200",
                                onclick: () => m.route.set('/loan-installments')
                            }, [
                                m("i", { class: "ri-arrow-left-line mr-2" }),
                                "Kembali ke Angsuran"
                            ]),
                            m("h1", { class: "text-2xl font-bold text-gray-900 dark:text-white" }, "Daftar Tanda Terima")
                        ]),
                        m("button", {
                            class: "bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2",
                            onclick: () => this.generateAllReceiptsPDF()
                        }, [
                            m("i", { class: "ri-printer-line" }),
                            "Cetak Semua"
                        ])
                    ])
                ])
            ]),

            // Main Content
            m("div", { class: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" }, [
                this.loading ? m("div", { class: "text-center py-12" }, [
                    m("i", { class: "ri-loader-4-line animate-spin text-4xl text-blue-500 mb-4" }),
                    m("p", { class: "text-gray-600 dark:text-gray-300" }, "Memuat data tanda terima...")
                ]) : this.paidInstallments.length === 0 ? m("div", { class: "text-center py-12" }, [
                    m("i", { class: "ri-file-list-3-line text-4xl text-gray-400 dark:text-gray-500 mb-4" }),
                    m("p", { class: "text-gray-600 dark:text-gray-300" }, "Tidak ada data tanda terima"),
                    m("button", {
                        class: "mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200",
                        onclick: () => m.route.set('/loan-installments')
                    }, "Kembali ke Halaman Angsuran")
                ]) : [
                    m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden" }, [
                        m("div", { class: "overflow-x-auto" }, [
                            m("table", { class: "w-full text-sm text-left text-gray-500 dark:text-gray-400" }, [
                                m("thead", { class: "text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400" }, [
                                    m("tr", [
                                        m("th", { class: "px-6 py-3" }, "No"),
                                        m("th", { class: "px-6 py-3" }, "Nasabah"),
                                        m("th", { class: "px-6 py-3" }, "Angsuran"),
                                        m("th", { class: "px-6 py-3" }, "Jumlah Dibayar"),
                                        m("th", { class: "px-6 py-3" }, "Tanggal Bayar"),
                                        m("th", { class: "px-6 py-3" }, "Status"),
                                        m("th", { class: "px-6 py-3" }, "Aksi")
                                    ])
                                ]),
                                m("tbody", [
                                    this.paidInstallments.map((installment, index) => m("tr", {
                                        class: "bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                                    }, [
                                        m("td", { class: "px-6 py-4 font-medium text-gray-900 dark:text-white" }, index + 1),
                                        m("td", { class: "px-6 py-4" }, [
                                            m("div", { class: "font-medium text-gray-900 dark:text-white" }, installment.customer_name),
                                            m("div", { class: "text-sm text-gray-500 dark:text-gray-400" }, `Reg: ${installment.customer_no_reg}`)
                                        ]),
                                        m("td", { class: "px-6 py-4" }, `Ke-${installment.installment_number || 'N/A'}`),
                                        m("td", { class: "px-6 py-4 font-medium text-green-600 dark:text-green-400" }, this.formatCurrency(installment.paid_amount || 0)),
                                        m("td", { class: "px-6 py-4" }, this.formatDate(installment.payment_date)),
                                        m("td", { class: "px-6 py-4" }, [
                                            m("span", {
                                                class: `px-2 py-1 text-xs rounded-full ${installment.status === 'LUNAS' ?
                                                    'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                                    }`
                                            }, installment.status || 'SEBAGIAN')
                                        ]),
                                        m("td", { class: "px-6 py-4" }, [
                                            m("button", {
                                                class: "bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs transition-colors duration-200",
                                                onclick: () => this.generatePDF(installment)
                                            }, [
                                                m("i", { class: "ri-printer-line mr-1" }),
                                                "Cetak"
                                            ])
                                        ])
                                    ]))
                                ])
                            ])
                        ])
                    ])
                ]
            ])
        ]);
    }
};

export default Receipts;